import { UNVERIFIED_PHONE_WORK_ITEM_TITLE } from '@orn/global/workItem/workItemTitles.js';

import { WorkItemUserCategory } from '../../__generated__/globalTypes';
import { PATHS } from '../../paths';

import type { AlertRowAction } from './alertAction';
import type { WorkItemDetailsFragment } from '../../components/alerts/__generated__/workItemFragment.gql';

/** The fields a work-item row action derives from. */
export type AlertActionWorkItem = Pick<WorkItemDetailsFragment, 'category' | 'bankConnection' | 'title'>;

/**
 * A CONFIG work item carries no subtype, so its relation is the discriminator
 * and its title breaks the tie among the link-less ones: a `bankConnection`
 * names the connection to reconnect, and phone verification is the one
 * condition with no subject row whose fix lives in settings.
 *
 * **Recognized rather than assumed**, which is the part that must not regress.
 * The title match is what keeps the settings route to the condition settings
 * can actually fix; anything else link-less — the email card a loved one
 * answers through the assistant, an authored CONFIG row naming no condition at
 * all — has no fix to run from here and falls through to `null`, the same
 * answer `getAlertRowAction` gives a subtype-less CONFIG alert. A new CONFIG
 * condition with a fix in the app carries a relation or a branch here; one
 * answered elsewhere needs neither.
 */
const configAction = (workItem: AlertActionWorkItem): AlertRowAction | null => {
  if (workItem.bankConnection) {
    return { kind: 'reconnectBank', bankConnectionId: workItem.bankConnection.id };
  }
  return workItem.title === UNVERIFIED_PHONE_WORK_ITEM_TITLE ? { kind: 'navigate', path: PATHS.SETTINGS } : null;
};

/**
 * A new `WorkItemUserCategory` does not compile until it picks an action.
 * `null` means the row has none of its own, and the click belongs to whatever
 * the surface does with an ordinary alert.
 */
const ACTION_BY_CATEGORY: Record<WorkItemUserCategory, (workItem: AlertActionWorkItem) => AlertRowAction | null> = {
  [WorkItemUserCategory.Config]: configAction,
  [WorkItemUserCategory.Update]: () => null,
  [WorkItemUserCategory.Reminder]: () => null,
};

/**
 * The action a work-item row click runs, or `null` when the category has none
 * — the twin of `getAlertRowAction`, which answers `null` for every adapted
 * work item because the twin carries no subtype. Route work-item clicks here.
 */
export function getWorkItemAlertRowAction(workItem: AlertActionWorkItem): AlertRowAction | null {
  return ACTION_BY_CATEGORY[workItem.category](workItem);
}
